/**
 * Requests tab (BON-161) — admin triage queue for item requests. Approve
 * (creates the item) or reject (with a reason). FIFO, pending first.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  fetchItemRequests,
  approveItemRequest,
  rejectItemRequest,
  type ItemRequest,
} from '../../../api/inventory-catalog';

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'pending'
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : status === 'approved'
        ? 'text-moss-700 bg-moss-50 border-moss-200'
        : 'text-stone-500 bg-stone-50 border-stone-200';
  return (
    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${tone}`}>
      {status}
    </span>
  );
}

function RequestRow({
  r,
  busy,
  onApprove,
  onReject,
}: {
  r: ItemRequest;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3 text-sm">
      <div className="min-w-0">
        <div className="font-medium text-stone-900 dark:text-stone-100">
          {r.proposedName} <span className="text-xs text-stone-400">· {r.category}</span>
        </div>
        <div className="text-xs text-stone-500">
          {r.requestedByName}
          {r.requestNotes ? ` — ${r.requestNotes}` : ''}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <StatusBadge status={r.status} />
        {r.status === 'pending' && (
          <>
            <button
              onClick={onApprove}
              disabled={busy}
              className="text-xs text-moss-600 hover:underline disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              className="text-xs text-red-600 hover:underline disabled:opacity-50"
            >
              Reject
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export default function RequestsTab() {
  const [requests, setRequests] = useState<ItemRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchItemRequests()
      .then((r) => setRequests(r.requests))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function handleApprove(req: ItemRequest) {
    setBusyId(req.id);
    try {
      await approveItemRequest(req.id);
      load();
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(req: ItemRequest) {
    const reason = window.prompt(`Reject "${req.proposedName}" — reason?`);
    if (!reason) return;
    setBusyId(req.id);
    try {
      await rejectItemRequest(req.id, reason);
      load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 dark:border-stone-700">
        <h2 className="text-base font-semibold text-bark-900 dark:text-stone-100">Item requests</h2>
        <span className="ml-auto text-xs text-stone-400">
          {requests.filter((r) => r.status === 'pending').length} pending
        </span>
      </div>

      {loading ? (
        <div className="py-8 text-center text-stone-500 text-sm">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="py-8 text-center text-stone-500 text-sm">No requests yet</div>
      ) : (
        <ul className="divide-y divide-stone-100 dark:divide-stone-700/50">
          {requests.map((r) => (
            <RequestRow
              key={r.id}
              r={r}
              busy={busyId === r.id}
              onApprove={() => handleApprove(r)}
              onReject={() => handleReject(r)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
